import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";
import i18n from "@/i18n";


const CATEGORY_KINDS = ["good", "service", "operation"];


function buildTree(categories) {
    const map = {};
    for (const cat of categories) {
        map[cat.id] = {...cat, children: []};
    }
    const roots = [];
    for (const cat of categories) {
        const node = map[cat.id];
        if (cat.parent && map[cat.parent]) {
            map[cat.parent].children.push(node);
        } else {
            roots.push(node);
        }
    }
    // Fill recursive goods_count = own goods_count + sum of children's total_goods_count.
    function fillCount(node) {
        let total = node.goods_count || 0;
        for (const child of node.children) total += fillCount(child);
        node.total_goods_count = total;
        return total;
    }
    for (const root of roots) fillCount(root);
    return roots;
}


function getDescendantIds(categoryId, allCategories) {
    const childrenMap = {};
    for (const cat of allCategories) {
        if (!cat.parent) continue;
        if (!childrenMap[cat.parent]) childrenMap[cat.parent] = [];
        childrenMap[cat.parent].push(cat.id);
    }
    const result = new Set([categoryId]);
    const queue = [categoryId];
    while (queue.length > 0) {
        const current = queue.pop();
        for (const childId of (childrenMap[current] || [])) {
            if (!result.has(childId)) {
                result.add(childId);
                queue.push(childId);
            }
        }
    }
    return result;
}


function buildAuthHeaders(rootState, {withCsrf = false} = {}) {
    const headers = {
        role: rootState.personalData.role,
        companyId: rootState.company.companyData?.token,
    };
    if (withCsrf) {
        headers["Content-Type"] = "application/json";
        headers["X-CSRFToken"] = globalMethods.getCookie("csrftoken");
    }
    return headers;
}

function makeDetailedErrorHandler(fallbackKey) {
    return (data) => {
        const message = typeof data?.detail === "string" ? data.detail : i18n.global.t(fallbackKey);
        notify(message, "red", 5000);
        return {successfully: false, detail: data?.detail};
    };
}


export default {
    namespaced: true,
    state: {
        categories: [],
        selectedCategoryId: null,
        selectedKindFilter: null,
    },
    getters: {
        tree: (state) => (kind) => {
            return buildTree(state.categories.filter(c => c.kind === kind));
        },
    },
    mutations: {
        setCategories(state, categories) {
            state.categories = categories;
        },
        addCategory(state, category) {
            state.categories = [...state.categories, category];
        },
        updateCategory(state, updated) {
            state.categories = state.categories.map(c => c.id === updated.id ? updated : c);
        },
        removeCategory(state, id) {
            state.categories = state.categories.filter(c => c.id !== id);
        },
        removeCategoryIds(state, ids) {
            const idSet = new Set(ids);
            state.categories = state.categories.filter(c => !idSet.has(c.id));
        },
        setSelectedCategoryId(state, id) {
            state.selectedCategoryId = id;
        },
        setSelectedKindFilter(state, kind) {
            state.selectedKindFilter = kind;
        },
    },
    actions: {
        async loadAllCategories({commit, rootState}, params = {}) {
            const {shopId = null} = params;
            const allCategories = [];
            const failedKinds = [];
            let systemFailure = null;
            for (const kind of CATEGORY_KINDS) {
                const options = {
                    method: "GET",
                    headers: buildAuthHeaders(rootState),
                    credentials: "include",
                };
                const queryParams = new URLSearchParams({kind});
                if (shopId && shopId !== "all" && shopId !== "main") {
                    queryParams.set("shop-id", shopId);
                }
                const result = await globalMethods.sendRequestToGetJson({
                    url: `${URLS.MODEL.GOOD_CATEGORY.GOOD_CATEGORIES_URL}?${queryParams.toString()}`,
                    options,
                    successfulHandler: (data) => ({successfully: true, data: data.data}),
                });
                if (result.successfully && result.data) {
                    allCategories.push(...result.data);
                    continue;
                }
                // globalMethods already toasted; bail so our generic message
                // doesn't talk over the real cause.
                if (result.authExpired || result.planExpired || result.maintenance || result.permissionDenied) {
                    systemFailure = result;
                    break;
                }
                failedKinds.push(kind);
            }
            // Skip the commit on system failure: the partial list would clobber
            // the previous valid one, and the user is being toasted/redirected
            // anyway. They'll re-fetch on the next sign-in.
            if (systemFailure) {
                return systemFailure;
            }
            commit("setCategories", allCategories);
            if (failedKinds.length > 0) {
                console.error("loadAllCategories: failed kinds", failedKinds);
                notify(i18n.global.t("Failed to load some categories. Please refresh."), "red", 5000);
                return {successfully: false, failedKinds};
            }
            return {successfully: true};
        },
        async createCategory({commit, rootState}, {name, kind, parent}) {
            const options = {
                method: "POST",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                credentials: "include",
                body: JSON.stringify({name, kind, parent}),
            };
            const successfulHandler = (data) => {
                commit("addCategory", data.data);
                notify(i18n.global.t("Category successfully added"), "black");
                return {successfully: true, data: data.data};
            };
            return globalMethods.sendRequestToGetJson({
                url: URLS.MODEL.GOOD_CATEGORY.GOOD_CATEGORIES_URL,
                options,
                successfulHandler,
                errorHandler: makeDetailedErrorHandler("Failed to save category"),
            });
        },
        async updateCategory({commit, rootState}, {id, name, parent, is_active}) {
            const body = {};
            if (name !== undefined) body.name = name;
            if (parent !== undefined) body.parent = parent;
            if (is_active !== undefined) body.is_active = is_active;
            const options = {
                method: "PUT",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                credentials: "include",
                body: JSON.stringify(body),
            };
            const successfulHandler = (data) => {
                commit("updateCategory", data.data);
                notify(i18n.global.t("Category successfully updated"), "black");
                return {successfully: true, data: data.data};
            };
            return globalMethods.sendRequestToGetJson({
                url: URLS.MODEL.GOOD_CATEGORY.GOOD_CATEGORY_URL.replace("<ID>", id),
                options,
                successfulHandler,
                errorHandler: makeDetailedErrorHandler("Failed to save category"),
            });
        },
        async deleteCategory({commit, rootState}, {id, mode}) {
            const options = {
                method: "DELETE",
                headers: buildAuthHeaders(rootState, {withCsrf: true}),
                credentials: "include",
                body: JSON.stringify({mode: mode || "orphan"}),
            };
            const successfulHandler = () => {
                const {categories, selectedCategoryId} = rootState.goodCategory;
                let affectedIds;
                if (mode === "cascade") {
                    affectedIds = getDescendantIds(id, categories);
                    commit("removeCategoryIds", Array.from(affectedIds));
                } else {
                    const target = categories.find(c => c.id === id);
                    const newParent = target ? target.parent : null;
                    for (const cat of categories) {
                        if (cat.parent === id) {
                            commit("updateCategory", {...cat, parent: newParent});
                        }
                    }
                    commit("removeCategory", id);
                    affectedIds = new Set([id]);
                }
                if (selectedCategoryId !== null && affectedIds.has(selectedCategoryId)) {
                    commit("setSelectedCategoryId", null);
                }
                notify(i18n.global.t("Category successfully deleted"), "black");
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({
                url: URLS.MODEL.GOOD_CATEGORY.GOOD_CATEGORY_URL.replace("<ID>", id),
                options,
                successfulHandler,
                errorHandler: makeDetailedErrorHandler("Failed to delete category"),
            });
        },
        selectCategory({commit, dispatch}, categoryId) {
            commit("setSelectedCategoryId", categoryId);
            dispatch("good/searchGoods", "", {root: true});
        },
    },
};
